import React from "react";
import styles from './ScrollingText.module.scss';

interface ScrollingTextProps {
    text: string;
    className?: string;
    speedPxPerSecond?: number;
    startDelayMs?: number;
}

export const ScrollingText: React.FC<ScrollingTextProps> = ({
    text,
    className,
    speedPxPerSecond = 10,
    startDelayMs = 1000,
}) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const textRef = React.useRef<HTMLSpanElement>(null);

    const [animate, setAnimate] = React.useState(false);
    const [duration, setDuration] = React.useState(0);
    const [translateX, setTranslateX] = React.useState(0);

    const measure = React.useCallback(() => {
        const container = containerRef.current;
        const textEl = textRef.current;

        if (!container || !textEl) return;

        const containerWidth = container.offsetWidth;
        const textWidth = textEl.scrollWidth;

        if (textWidth > containerWidth) {
            const distance = textWidth - containerWidth;

            setTranslateX(-distance);
            setDuration(Math.max(distance / speedPxPerSecond, 1));
            setAnimate(true);
        } else {
            setAnimate(false);
        }
    }, [speedPxPerSecond]);

    React.useEffect(() => {
        const raf = requestAnimationFrame(() => {
            measure();
        });

        return () => cancelAnimationFrame(raf);
    }, [text, measure]);

    React.useEffect(() => {
        const ro = new ResizeObserver(measure);
        if (containerRef.current) {
            ro.observe(containerRef.current);
        }
        return () => ro.disconnect();
    }, [measure]);

    return (
        <div
            ref={containerRef}
            className={`${styles.container} ${className ?? ''}`}
        >
            <span
                ref={textRef}
                className={`${styles.text} ${animate ? styles.animate : ''}`}
                style={
                    animate
                        ? ({
                            '--duration': `${duration}s`,
                            '--translate-x': `${translateX}px`,
                            '--delay': `${startDelayMs}ms`,
                        } as React.CSSProperties)
                        : undefined
                }
            >
                {text}
            </span>
        </div>
    );
};
